"use client";
import { Command, CommandGroup, CommandItem } from "cmdk";
import Fuse from "fuse.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";

import { useAutoClose } from "~/hooks/useAutoClose";
import { cn } from "~/utils/fontsConfig";
import { Input, InputProps } from "./Input";

const fuseOptions = {
    keys: ["displayValue"],
    threshold: 0.3,
};

interface SearchProps<T> extends InputProps {
    label?: string;
    name: string;
    placeholder?: string;
    values?: T[];
    query?: string;
    setQuery?: (query: string) => void;
    setSelected?: (selected: T) => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    className?: string;
    control?: any;
}

const AutoComplete = <T extends { displayValue: string }>({
    label,
    name,
    values,
    query,
    placeholder = "",
    setQuery,
    setSelected,
    onChange,
    className,
    control,
}: SearchProps<T>) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useAutoClose(false, ref);
    const [internalQuery, setInternalQuery] = useState<string>("");

    const fuse = useMemo(() => {
        return new Fuse(values || [], fuseOptions);
    }, [values, fuseOptions]);

    const [filteredResults, setFilteredResults] = useState(values || []);

    const debouncedSearch = useCallback(() => {
        const debounceTimeout = setTimeout(() => {
            const results = internalQuery
                ? fuse.search(internalQuery).map(({ item }) => item)
                : values || [];

            setFilteredResults(results);
            setIsOpen(results.length > 0 && internalQuery.length > 0);
        }, 300);

        return () => clearTimeout(debounceTimeout);
    }, [values, internalQuery, fuse]);

    return (
        <div ref={ref} className={cn("relative flex flex-col", className)}>
            <div className={cn("flex flex-col")}>
                {label && <label className="text-inherit">{label}</label>}
                {control ? (
                    <Controller
                        name={name}
                        control={control}
                        defaultValue={internalQuery}
                        render={({ field }) => (
                            <Input
                                placeholder={placeholder}
                                value={field.value}
                                className={cn("bg-v3-neutral", className)}
                                onChange={(e) => {
                                    field.onChange(e);
                                    onChange && onChange(e);
                                    setQuery && setQuery(e.target.value);
                                    setInternalQuery(e.target.value);
                                    debouncedSearch();
                                }}
                            />
                        )}
                    />
                ) : (
                    <Input
                        placeholder={placeholder}
                        value={internalQuery}
                        className={cn("bg-v3-neutral", className)}
                        onChange={(e) => {
                            onChange && onChange(e);
                            setInternalQuery(e.target.value);
                            setQuery && setQuery(e.target.value);
                            debouncedSearch();
                        }}
                    />
                )}
            </div>
            {isOpen && (
                <Command shouldFilter={false}>
                    <CommandGroup className="absolute z-[100] h-max max-h-60 w-max min-w-full overflow-x-hidden overflow-y-scroll rounded-b-md border-[1px] border-t-0 border-white p-0">
                        {filteredResults.map((v) => {
                            const displayValue = v.displayValue;
                            const queryLowerCase =
                                internalQuery.toLocaleLowerCase();
                            const index = displayValue
                                .toLocaleLowerCase()
                                .indexOf(queryLowerCase);
                            const beforeMatch = displayValue.slice(0, index);
                            const matchedText = displayValue.slice(
                                index,
                                index + internalQuery.length,
                            );
                            const afterMatch = displayValue.slice(
                                index + internalQuery.length,
                            );

                            return (
                                <CommandItem
                                    className="w-full cursor-pointer bg-v3-neutral p-3 text-white aria-selected:bg-v3-neutral"
                                    key={v.displayValue}
                                    onSelect={() => {
                                        setSelected && setSelected(v);
                                        setInternalQuery(v.displayValue);
                                        setIsOpen(false);
                                    }}
                                >
                                    <>
                                        {beforeMatch}
                                        <strong>{matchedText}</strong>
                                        {afterMatch}
                                    </>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                </Command>
            )}
        </div>
    );
};

export default AutoComplete;
